/**
 * Значения для шаблона ответа правила (004, data-model А.2). Шаблон — ICU MessageFormat из
 * `@tabel/engine/meta` (`ANSWERS`); его отрисовывают сервер (печать правил) и интерфейс (карточка правила). Здесь —
 * общая часть: разбор аргумента и значения аргументов по значениям полей. Функции чистые, без зависимостей.
 *
 * Аргумент шаблона:
 * - `кубик.поле` — значение поля: у варианта и да/нет — ключ `select` (`icuKey`), у остальных — текст значения;
 * - `кубик.поле~значение` — `true`, если в списке поля есть это значение, иначе `false` (`select`);
 * - `кубик.поле#` — число элементов списка или строк таблицы со значением (`plural` с `=0`).
 */

/** Значения вариантов, которые не годятся в ключ `select` ICU (знаки сравнения, двоеточие), и их ключи. */
export const ICU_KEY_ALIASES: Readonly<Record<string, string>> = {
  '≥': 'не_меньше',
  '>': 'больше',
  '≤': 'не_больше',
  '<': 'меньше',
  'чч:мм': 'чч_мм',
};

/** Ключ `select` ICU для значения поля: строка значения или её замена из `ICU_KEY_ALIASES`. */
export function icuKey(value: unknown): string {
  const s = String(value);
  return ICU_KEY_ALIASES[s] ?? s;
}

/** Ключ годится для `select` ICU: буквы, цифры и `_`. */
export const ICU_KEY_RE = /^[\p{L}\p{N}_]+$/u;

export interface AnswerArg {
  readonly cube: string;
  readonly field: string;
  /** `кубик.поле` — ключ значения в `values`. */
  readonly key: string;
  /** `~значение`: есть ли значение в списке. */
  readonly option?: string;
  /** `#`: число элементов. */
  readonly count?: true;
}

const ARG_RE = /^([^.~#]+)\.([^~#]+)(?:~(.+)|(#))?$/u;

/** Разбор аргумента шаблона; неверная запись — `undefined`. */
export function parseAnswerArg(spec: string): AnswerArg | undefined {
  const m = ARG_RE.exec(spec);
  if (!m) return undefined;
  const [, cube, field, option, count] = m as unknown as [string, string, string, string?, string?];
  return {
    cube,
    field,
    key: `${cube}.${field}`,
    ...(option !== undefined ? { option } : {}),
    ...(count !== undefined ? { count: true as const } : {}),
  };
}

const isMap = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** Число элементов: длина списка или число строк таблицы со значением (не `false`, не пусто). */
function countOf(v: unknown): number {
  if (Array.isArray(v)) return v.length;
  if (isMap(v)) return Object.values(v).filter((x) => x !== false && x !== null && x !== undefined).length;
  return 0;
}

export interface AnswerValueSource {
  /** Значение поля по ключу `кубик.поле`. */
  readonly get: (key: string) => unknown;
  /** Поле — вариант или да/нет: его значение идёт в `select` ключом. */
  readonly isChoice: (key: string) => boolean;
  /** Текст значения поля: число с единицей, время, список. */
  readonly text: (key: string, value: unknown) => string;
}

/** Значения аргументов шаблона для `IntlMessageFormat.format`. */
export function answerValues(
  args: Readonly<Record<string, string>>,
  src: AnswerValueSource,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [name, spec] of Object.entries(args)) {
    const a = parseAnswerArg(spec);
    if (!a) continue;
    const v = src.get(a.key);
    if (a.option !== undefined)
      out[name] = Array.isArray(v) && v.map(String).includes(a.option) ? 'true' : 'false';
    else if (a.count) out[name] = countOf(v);
    else out[name] = src.isChoice(a.key) ? icuKey(v) : src.text(a.key, v);
  }
  return out;
}

/** Текст после отрисовки: пустые ветки `select` дают лишние пробелы — убрать. */
export function tidyAnswer(text: string): string {
  return text
    .replace(/\s+/gu, ' ')
    .replace(/\s+([.,])/gu, '$1')
    .trim();
}
